import { useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";
import { useRemoteAmazonProfiles } from "./useAmazonProfiles";

const InvalidAdsProfilesWarning = () => {
  const { t } = useTranslation(["onboarding"]);
  const { data: amazonProfiles } = useRemoteAmazonProfiles();

  const suggestedReason = useMemo(() => {
    if (amazonProfiles?.length && amazonProfiles.every((profile) => profile.is_kdp)) {
      return t(
        ($) =>
          $.onboarding[
            "All connected profiles are Kindle Direct Publishing (KDP) profiles, which are not supported by Trellis."
          ],
      );
    }

    if (amazonProfiles?.length && amazonProfiles.every((profile) => !profile.valid_payment_method)) {
      return t(($) => $.onboarding["All connected profiles have invalid or expired payment methods."]);
    }
  }, [amazonProfiles, t]);

  return (
    <VStack
      css={{
        justifyContent: "flex-start",
        alignItems: "flex-start",
        paddingRight: "xlarge",
        fontSize: "medium",
        fontWeight: "normal",
      }}
    >
      <p>
        {t(
          ($) =>
            $.onboarding[
              "The Amazon Advertising API has been successfully authorized, but no advertising profiles are available to connect. {reason}"
            ],
          { reason: suggestedReason ?? t(($) => $.onboarding["This is usually caused by one of the following:"]) },
        )}
      </p>

      {!suggestedReason && (
        <ol className={css({ display: "flex", gap: "medium", flexDirection: "column" })}>
          <li>
            {t(
              ($) =>
                $.onboarding[
                  "The connected Amazon account doesn’t have access to any profiles that have previously run ads. At least one ad campaign must exist before a profile becomes available through the Amazon Advertising API."
                ],
            )}
          </li>
          <li>
            {t(
              ($) =>
                $.onboarding[
                  "The connected Amazon account only contains Kindle Direct Publishing (KDP) profiles, which are not currently supported by Trellis."
                ],
            )}
          </li>
          <li>
            <Trans
              t={t}
              i18nKey={($) =>
                $.onboarding[
                  "All available profiles have only <strong>View</strong> permissions. <strong>View & Edit</strong> access is required to retrieve profiles from the Amazon Advertising API."
                ]
              }
              components={{ strong: <strong /> }}
            />
          </li>
          <li>
            {t(
              ($) =>
                $.onboarding[
                  "All profiles have invalid or expired payment methods. You can resolve this by updating your payment details in the Amazon Advertising console."
                ],
            )}
          </li>
          <li>
            {t(
              ($) =>
                $.onboarding[
                  "All profiles linked to this Amazon account are already connected to Trellis. Each advertising profile can only be linked once."
                ],
            )}
          </li>
        </ol>
      )}

      <p>
        <Trans
          t={t}
          i18nKey={($) =>
            $.onboarding[
              "Please review your Amazon Advertising account and ensure at least one supported profile is available. If the issue persists, <customerSuccessLink>contact Customer Success</customerSuccessLink> for assistance."
            ]
          }
          components={{
            customerSuccessLink: (
              <a
                href="https://gotrellis.com/book-a-demo/"
                target="_blank"
                rel="noreferrer"
                className={css({ color: "trellisPalette.0", fontWeight: "600" })}
              />
            ),
          }}
        />
      </p>
    </VStack>
  );
};

export default InvalidAdsProfilesWarning;
